<template>
  <div class="advanced-store-select">
    <el-popover
      v-model:visible="visible"
      trigger="click"
      placement="bottom-start"
      :width="effectivePanelWidth"
      :offset="6"
      :show-arrow="false"
      popper-class="store-scope-select-popper"
      @show="handleShow"
      @hide="handleHide"
    >
      <template #reference>
        <el-input
          :style="{ width: width + 'px' }"
          :model-value="displayText"
          readonly
          clearable
          :placeholder="placeholder"
          @clear.stop="clear"
        >
          <template
            v-if="title"
            #prefix
          >
            {{ title }}：
          </template>
          <template #suffix>
            <el-icon
              v-if="displayText"
              class="el-input__clear selector-clear-icon"
              @mousedown.stop.prevent
              @click.stop="clear"
            >
              <CircleClose />
            </el-icon>
            <el-icon :class="['selector-arrow-icon', { 'selector-arrow-has-value': displayText }]">
              <ArrowDown />
            </el-icon>
          </template>
        </el-input>
      </template>

      <div
        class="store-scope-panel"
        :style="{ width: effectivePanelWidth + 'px' }"
      >
        <div
          v-if="availableTabs.length > 1"
          class="scope-tabs"
        >
          <button
            v-for="tab in availableTabs"
            :key="tab.type"
            type="button"
            :class="['scope-tab', { active: tab.type === activeType }]"
            @click="switchType(tab.type)"
          >
            {{ tab.label }}
          </button>
        </div>

        <el-input
          v-model="keyword"
          class="scope-search"
          clearable
          :placeholder="activeTab?.placeholder || '请输入关键字'"
          @input="handleKeywordInput"
          @clear="searchOptions"
        />

        <div
          ref="optionListRef"
          v-scroll-bottom="loadNextPage"
          class="scope-list"
        >
          <div
            v-for="item in optionList"
            :key="`${item.type}-${item.id}`"
            :class="['scope-option', { selected: isSelected(item) }]"
            @click="toggleOption(item)"
          >
            <span class="scope-option-name">{{ item.name }}</span>
            <el-icon
              v-if="isSelected(item)"
              class="scope-option-check"
            >
              <Check />
            </el-icon>
          </div>

          <el-empty
            v-if="!loading && !optionList.length"
            :image-size="60"
            description="暂无数据"
          />
          <div
            v-if="loading"
            class="scope-state"
          >
            加载中...
          </div>
          <div
            v-else-if="optionList.length && noMore"
            class="scope-state"
          >
            没有更多了
          </div>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { ArrowDown, Check, CircleClose } from '@element-plus/icons-vue';
import request from '../../utils/request.ts';

type ScopeType = 'shop' | 'group' | 'label' | 'area' | string;

interface ScopeTab {
  type: ScopeType;
  label: string;
  url: string;
  idKey: string;
  nameKey: string;
  keywordKey?: string;
  placeholder?: string;
}

interface StoreItem {
  id?: string | number;
  name?: string;
  type?: ScopeType;
  typeName?: string;
  raw?: any;
  [key: string]: any;
}

const DEFAULT_TABS: ScopeTab[] = [
  {
    type: 'shop',
    label: '门店',
    url: '/b/common/shop/options',
    idKey: 'shopId',
    nameKey: 'name',
    keywordKey: 'keyword',
    placeholder: '门店名称/编码/ID',
  },
  {
    type: 'group',
    label: '机构',
    url: '/b/common/shop-group/options',
    idKey: 'groupId',
    nameKey: 'name',
    keywordKey: 'keyword',
    placeholder: '机构名称',
  },
  {
    type: 'label',
    label: '标签',
    url: '/b/common/shop-label/options',
    idKey: 'labelId',
    nameKey: 'name',
    keywordKey: 'keyword',
    placeholder: '标签名称',
  },
  {
    type: 'area',
    label: '区域',
    url: '/b/common/shop-area/options',
    idKey: 'areaId',
    nameKey: 'name',
    keywordKey: 'keyword',
    placeholder: '区域名称',
  },
];

const props = withDefaults(
  defineProps<{
    modelValue?: StoreItem[] | StoreItem | Array<string | number> | string | number | null;
    title?: string;
    width?: number;
    panelWidth?: number;
    placeholder?: string;
    multiple?: boolean;
    pageSize?: number;
    optionTypes?: ScopeType[];
    onlyType?: ScopeType;
    oneTypeOnly?: boolean;
    tabs?: Partial<ScopeTab>[];
    linkParams?: Record<string, any>;
    statusList?: Array<string | number>;
  }>(),
  {
    modelValue: () => [],
    title: '',
    width: 230,
    placeholder: '请选择',
    multiple: true,
    pageSize: 10,
    optionTypes: () => ['shop'],
    onlyType: '',
    oneTypeOnly: false,
    tabs: () => [],
    linkParams: () => ({}),
    statusList: () => [1, 2, 3, 4, 5],
  },
);

const effectivePanelWidth = computed(() => props.panelWidth || props.width);

const emits = defineEmits<{
  (e: 'update:modelValue', value: StoreItem[]): void;
  (e: 'change', value: StoreItem[]): void;
  (e: 'close', value: StoreItem[]): void;
}>();

const visible = ref(false);
const activeType = ref<ScopeType>('shop');
const keyword = ref('');
const optionList = ref<StoreItem[]>([]);
const optionListRef = ref<HTMLDivElement>();
const pageNum = ref(1);
const total = ref(0);
const loading = ref(false);
const noMore = ref(false);
let keywordTimer: ReturnType<typeof setTimeout> | undefined;
let requestVersion = 0;

// 关闭立即重置，避免隐藏动画期间的触底事件和旧响应污染下次展开。
watch(
  visible,
  (isVisible) => {
    if (isVisible) return;
    keyword.value = '';
    resetOptions();
  },
  { flush: 'sync' },
);
onBeforeUnmount(() => {
  visible.value = false;
  resetOptions();
});

const tabList = computed<ScopeTab[]>(() => {
  return DEFAULT_TABS.map((tab) => ({
    ...tab,
    ...(props.tabs.find((item) => item.type === tab.type) || {}),
  }));
});

const availableTabs = computed(() => {
  const types = props.onlyType ? [props.onlyType] : props.optionTypes;
  return tabList.value.filter((tab) => types.includes(tab.type));
});

const activeTab = computed(() => availableTabs.value.find((tab) => tab.type === activeType.value) || availableTabs.value[0]);

const selectedList = computed<StoreItem[]>(() => normalizeModelValue(props.modelValue));

const displayText = computed(() =>
  selectedList.value
    .map((item) => item.name || item.id)
    .filter(Boolean)
    .join('、'));

watch(
  availableTabs,
  (tabs) => {
    if (!tabs.length) return;
    if (!tabs.some((tab) => tab.type === activeType.value)) {
      activeType.value = tabs[0].type;
    }
  },
  { immediate: true },
);

function normalizeModelValue(value: typeof props.modelValue): StoreItem[] {
  if (value === null || value === undefined || value === '') return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map((item: any) => {
    if (typeof item === 'object' && item !== null) {
      const type = item.type || activeType.value;
      const tab = tabList.value.find((tabItem) => tabItem.type === type);
      return {
        ...item,
        id: item.id ?? item.value ?? item[tab?.idKey || 'id'],
        name: item.name ?? item.label ?? item[tab?.nameKey || 'name'],
        type,
        typeName: item.typeName || tab?.label,
      };
    }
    return {
      id: item,
      name: String(item),
      type: activeType.value,
      typeName: activeTab.value?.label,
    };
  });
}

function handleShow() {
  nextTick(() => {
    if (visible.value && !optionList.value.length) {
      searchOptions();
    }
  });
}

function handleHide() {
  emits('close', selectedList.value);
}

function resetOptions() {
  if (keywordTimer) {
    clearTimeout(keywordTimer);
    keywordTimer = undefined;
  }
  requestVersion += 1;
  optionList.value = [];
  pageNum.value = 1;
  total.value = 0;
  noMore.value = false;
  loading.value = false;
  if (optionListRef.value) optionListRef.value.scrollTop = 0;
}

function switchType(type: ScopeType) {
  if (activeType.value === type) return;
  activeType.value = type;
  keyword.value = '';
  searchOptions();
}

function handleKeywordInput() {
  resetOptions();
  keywordTimer = setTimeout(() => {
    searchOptions();
  }, 300);
}

function buildQuery(tab: ScopeTab, requestedPage: number) {
  const query: Record<string, any> = {
    pageNum: requestedPage,
    pageSize: props.pageSize,
    reasonable: true,
    ...props.linkParams,
  };
  const keywordValue = keyword.value.trim();
  if (keywordValue) {
    query[tab.keywordKey || 'keyword'] = keywordValue;
  }
  if (tab.type === 'shop' && props.statusList?.length) {
    query.statusList = props.statusList;
  }
  return query;
}

async function searchOptions() {
  resetOptions();
  if (visible.value) await loadOptions(1);
}

async function loadNextPage() {
  // 清空列表也可能产生触底事件，只有已展开且已加载首屏才允许翻页。
  if (!visible.value || !optionList.value.length || loading.value || noMore.value) return;
  await loadOptions(pageNum.value + 1);
}

async function loadOptions(requestedPage: number) {
  const tab = activeTab.value;
  if (!visible.value || !tab?.url || loading.value) return;
  const version = ++requestVersion;
  loading.value = true;
  try {
    const query = buildQuery(tab, requestedPage);
    const res: any = await request({
      url: tab.url,
      method: 'post',
      params: {
        pageNum: query.pageNum,
        pageSize: query.pageSize,
        reasonable: query.reasonable,
      },
      data: query,
      headers: { repeatSubmit: false },
    });
    if (version !== requestVersion || !visible.value) return;
    const records = res?.records || res?.data?.records || res?.rows || [];
    total.value = Number(res?.total || res?.data?.total || records.length || 0);
    const nextList = records.map((item: any) => normalizeOption(item, tab)).filter((item: StoreItem) => item.id !== undefined && item.id !== null);
    optionList.value = requestedPage === 1 ? nextList : optionList.value.concat(nextList);
    // 成功后才推进页码，加载失败时仍可重试同一页。
    pageNum.value = requestedPage;
    noMore.value = optionList.value.length >= total.value || nextList.length < props.pageSize;
  } catch {
    // 请求封装统一提示失败，保留已加载选项和页码供重试。
  } finally {
    if (version === requestVersion) loading.value = false;
  }
}

function normalizeOption(item: any, tab: ScopeTab): StoreItem {
  const id = item?.[tab.idKey] ?? item?.id ?? item?.value;
  const name = item?.[tab.nameKey] ?? item?.label ?? item?.name ?? String(id || '');
  return {
    ...item,
    id,
    name,
    value: id,
    type: tab.type,
    typeName: tab.label,
    raw: item,
  };
}

function isSameOption(a: StoreItem, b: StoreItem) {
  return String(a.id) === String(b.id) && String(a.type || '') === String(b.type || '');
}

function isSelected(item: StoreItem) {
  return selectedList.value.some((selected) => isSameOption(selected, item));
}

function toggleOption(item: StoreItem) {
  let nextValue: StoreItem[] = [];
  if (props.multiple) {
    const baseList = props.oneTypeOnly ? selectedList.value.filter((selected) => selected.type === item.type) : selectedList.value;
    nextValue = isSelected(item) ? baseList.filter((selected) => !isSameOption(selected, item)) : baseList.concat(item);
  } else {
    nextValue = isSelected(item) ? [] : [item];
    visible.value = false;
  }
  emits('update:modelValue', nextValue);
  emits('change', nextValue);
}

function clear() {
  const alreadyClosed = !visible.value;
  visible.value = false;
  emits('update:modelValue', []);
  emits('change', []);
  // 多选筛选在 close 时提交；面板已关闭时清空也需要通知调用方。
  if (alreadyClosed) emits('close', []);
}
</script>

<style scoped lang="scss">
.advanced-store-select {
  display: inline-block;

  :deep(.el-input__wrapper) {
    grid-template-columns: max-content minmax(0, 1fr) 16px;
    column-gap: 8px;
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
  }

  :deep(.el-input__prefix),
  :deep(.el-input__prefix-inner) {
    flex-shrink: 0;
  }

  :deep(.el-input__inner) {
    min-width: 0;
    cursor: pointer;
  }

  :deep(.el-input__suffix),
  :deep(.el-input__suffix-inner) {
    width: 16px;
    min-width: 16px;
    justify-content: center;
  }

  :deep(.selector-clear-icon) {
    display: none;
    cursor: pointer;
  }

  :deep(.el-input__wrapper:hover .selector-clear-icon),
  :deep(.el-input__wrapper:focus-within .selector-clear-icon) {
    display: inline-flex;
  }

  :deep(.el-input__wrapper:hover .selector-arrow-icon.selector-arrow-has-value),
  :deep(.el-input__wrapper:focus-within .selector-arrow-icon.selector-arrow-has-value) {
    display: none;
  }

  &.custom-picker {
    :deep(.el-input__wrapper) {
      padding-right: 35px !important;
    }

  }
}

:global(.store-scope-select-popper.el-popover) {
  padding: 0 !important;
  border: 1px solid var(--el-border-color-light) !important;
  border-radius: var(--el-border-radius-base) !important;
  box-shadow: var(--el-box-shadow-light) !important;
}

:global(html.dark .store-scope-select-popper.el-popover) {
  --el-popover-bg-color: var(--el-bg-color-overlay);
  background: var(--el-bg-color-overlay) !important;
  color: var(--el-text-color-primary);
}

:global(html.dark .store-scope-select-popper.el-popover .store-scope-panel),
:global(html.dark .store-scope-select-popper.el-popover .scope-tabs) {
  background: var(--el-bg-color-overlay);
}

:global(html.dark .store-scope-select-popper.el-popover .scope-tab.active),
:global(html.dark .store-scope-select-popper.el-popover .scope-option:hover),
:global(html.dark .store-scope-select-popper.el-popover .scope-option.selected) {
  background: color-mix(in srgb, var(--el-color-primary) 20%, var(--el-bg-color-overlay));
}

.store-scope-panel {
  overflow: hidden;
  background: var(--el-bg-color-overlay);
  border-radius: var(--el-border-radius-base);

  .scope-tabs {
    display: flex;
    gap: 0;
    padding: 12px 12px 0;
    background: var(--el-bg-color-overlay);
    border-bottom: 1px solid var(--el-border-color-light);
  }

  .scope-tab {
    flex: 1;
    height: 32px;
    border: 0;
    border-radius: 3px 3px 0 0;
    background: transparent;
    color: var(--el-text-color-regular);
    font-size: 14px;
    cursor: pointer;
    transition:
      color 0.2s,
      background-color 0.2s;

    &:hover {
      color: var(--el-color-primary);
    }

    &.active {
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
      font-weight: 500;
    }
  }

  .scope-search-wrap {
    padding: 10px 0 6px;
  }

  .scope-search {
    display: block;
    width: 95%;
    margin: 4px auto 0;

    :deep(.el-input__wrapper) {
      width: 100%;
      min-height: 32px;
      border-radius: 4px;
      box-shadow: 0 0 0 1px var(--el-border-color) inset;
    }
  }

  .scope-list {
    max-height: 260px;
    padding: 2px 8px 10px;
    overflow-y: auto;
  }

  .scope-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 34px;
    padding: 0 12px;
    border-radius: 4px;
    color: var(--el-text-color-primary);
    font-size: 14px;
    line-height: 34px;
    cursor: pointer;
    transition:
      color 0.2s,
      background-color 0.2s;

    &:hover,
    &.selected {
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
    }
  }

  .scope-option-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .scope-option-check {
    flex-shrink: 0;
    margin-left: 8px;
  }

  .scope-state {
    padding: 8px 0;
    color: var(--el-text-color-secondary);
    font-size: 12px;
    text-align: center;
  }
}
</style>
